import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { logEventSafe } from '../analytics/analytics';
import { PageBackLink, PagePanel, PageShell } from './MarketingPageShell';

const PoliciesPage = () => {
  const location = useLocation();

  useEffect(() => {
    logEventSafe('page_view', {
      page_path: location.pathname,
      page_title: 'Policies'
    });
  }, [location.pathname]);

  const handleBackClick = () => {
    logEventSafe('click_back', {
      page_path: location.pathname
    });
  };

  return (
    <PageShell>
      <motion.div className="py-4" initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4, delay: 0.1 }}>
        <div className="mx-auto max-w-4xl space-y-6">
          <PageBackLink onClick={handleBackClick} />
          <PagePanel>
            <h1 className="mb-6 text-3xl font-bold text-white">Privacy Policy for Toolip</h1>
            <p className="mb-4 text-gray-300"><strong>Effective Date:</strong> 1/29/2025</p>
            <p className="mb-8 text-gray-300">
              Toolip ("we," "our," or "us") respects your privacy. This Privacy Policy explains what
              information we collect, how we use it, and the choices you have when using our services.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">1. Information We Collect</h2>
            <ul className="mb-4 list-inside list-disc text-gray-300">
              <li>Account information such as email address and display name</li>
              <li>Subscription and billing-related identifiers</li>
              <li>App usage and interaction data</li>
              <li>App preferences and settings</li>
              <li>Technical data such as IP address and device information</li>
            </ul>
            <p className="mb-6 text-gray-300">
              Account authentication is handled securely by Firebase Authentication. Toolip does not
              store your password.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">2. How We Use Your Information</h2>
            <ul className="mb-6 list-inside list-disc text-gray-300">
              <li>Create and manage your account</li>
              <li>Process subscriptions and payments</li>
              <li>Provide customer support</li>
              <li>Understand how features are used and improve the service</li>
              <li>Maintain security and prevent misuse</li>
            </ul>

            <h2 className="mb-2 text-xl font-semibold text-white">3. Analytics and Usage Data</h2>
            <p className="mb-6 text-gray-300">
              Toolip collects analytics data to understand how the app is used and to improve performance
              and features. This includes general interaction data, app preferences, and high-level usage patterns.
              When you are signed in, this information may be associated with your account to provide a more
              consistent experience. This association is removed when you sign out.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">4. Third-Party Services</h2>
            <p className="mb-6 text-gray-300">
              Toolip relies on trusted third-party services to operate. These include Firebase and Stripe.
              These providers process data in accordance with their own privacy policies.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">5. Data Deletion</h2>
            <p className="mb-6 text-gray-300">
              You can permanently delete your account and associated data from the account settings page.
              This includes your profile information and related content.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">6. Sharing Your Information</h2>
            <p className="mb-6 text-gray-300">
              We do not sell your personal information. Data may be shared with our service providers solely
              to operate and improve Toolip, and only as described in this policy.
            </p>

            <h2 className="mb-8 text-xl font-semibold text-white">7. Location</h2>
            <p className="mb-8 text-gray-300">Toolip is operated from New Hampshire, United States.</p>

            <hr className="my-8 border-gray-700" />

            <h1 className="mb-6 text-3xl font-bold text-white">Terms of Service for Toolip</h1>
            <p className="mb-4 text-gray-300"><strong>Effective Date:</strong> 7/20/2025</p>
            <p className="mb-8 text-gray-300">
              These Terms of Service ("Terms") govern your use of Toolip ("the Service"). By using
              the Service, you agree to these Terms.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">1. Description of Service</h2>
            <p className="mb-6 text-gray-300">
              Toolip is a productivity tool belt designed for professionals and teams. It provides a
              centralized platform for calculators, time tracking, note taking, trend analysis, and
              other utilities to enhance workflow.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">2. Accounts and Subscriptions</h2>
            <p className="mb-6 text-gray-300">
              Toolip uses Stripe for subscription billing. Users can cancel subscriptions at any time
              through the Stripe billing portal. Refunds are not automatic but may be reviewed case-by-case.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">3. Acceptable Use</h2>
            <ul className="mb-6 list-inside list-disc text-gray-300">
              <li>Use the service for illegal activities</li>
              <li>Attempt to reverse engineer or copy the platform</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Attempt to gain unauthorized access to systems</li>
            </ul>

            <h2 className="mb-2 text-xl font-semibold text-white">4. Availability and Support</h2>
            <p className="mb-6 text-gray-300">
              While we aim to keep Toolip reliable, uninterrupted availability is not guaranteed.
              Support is provided via email.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">5. Account Termination</h2>
            <p className="mb-6 text-gray-300">
              We may suspend or terminate accounts that violate these Terms or compromise the security
              or integrity of the Service.
            </p>

            <h2 className="mb-2 text-xl font-semibold text-white">6. Changes to These Terms</h2>
            <p className="mb-8 text-gray-300">
              We may update these Terms from time to time. Continued use of Toolip after changes means
              you accept the updated Terms.
            </p>

            <hr className="my-8 border-gray-700" />

            <p className="mb-2 text-gray-300">Questions? Contact us at <strong>app.toolip.contact@gmail.com</strong>.</p>
          </PagePanel>
        </div>
      </motion.div>
    </PageShell>
  );
};

export default PoliciesPage;
